import * as UserActions from "./actions";
import * as AppActions from "../app/actions";
import { createReducer, on } from "@ngrx/store";
import { jwtDecode } from "jwt-decode";
import { TokenPayload } from "src/app/interfaces";
import { Rewards, UserState, initialState } from "./state";
import { getError, getErrorParams } from "src/app/utils/get-error";
import { getProfileFormErrors, isProfileFormOutdated } from "src/app/utils/profile-form";
import { Platform } from "src/app/enums";

export const userReducer = createReducer(
  initialState,

  on(UserActions.setAuthToken, (state, { token }): UserState => {
    let userId: string;

    try {
      const payload = jwtDecode<TokenPayload>(token);
      userId = payload.id;
    } catch {}

    return { ...state, authToken: token, userId };
  }),

  on(UserActions.setDownloadToken, (state, { token }): UserState => ({
    ...state,
    downloadToken: token,
  })),

  on(UserActions.setVerifyToken, (state, { token }): UserState => ({
    ...state,
    verifyToken: token,
  })),

  on(UserActions.setCustomerData, (state, { customerData }): UserState => ({ ...state, customerData })),

  on(UserActions.logout, () => ({ ...initialState })),

  on(UserActions.resetError, (state): UserState => ({ ...state, error: "", errorParams: {} })),

  on(
    UserActions.register,
    (state): UserState => ({
      ...state,
      error: "",
      loading: true,
    }),
  ),

  on(UserActions.setLoading, (state, { loading }): UserState => ({ ...state, loading })),

  on(UserActions.getAppleCouponPass, (state): UserState => ({ ...state, downloadingCouponPass: Platform.ios })),
  on(UserActions.getGoogleCouponPass, (state): UserState => ({ ...state, downloadingCouponPass: Platform.android })),
  on(
    UserActions.getApplePassSuccess,
    UserActions.getApplePassFailure,
    UserActions.getGooglePassSuccess,
    UserActions.getGooglePassFailure,
    (state): UserState => ({ ...state, downloadingCouponPass: null }),
  ),

  on(
    UserActions.downloadPass,
    UserActions.checkCustomerExists,
    UserActions.sendPassToCustomer,
    UserActions.validateCustomer,
    UserActions.getApplePass,
    UserActions.getGooglePass,
    UserActions.getCustomerData,
    UserActions.getCustomerRewards,
    UserActions.sendValidationEmail,
    UserActions.updateCustomerPreferences,
    UserActions.updateCustomerProfile,
    UserActions.claimCustomerReward,
    UserActions.openShopLink,
    UserActions.getCustomerTransactions,
    (state): UserState => ({
      ...state,
      loading: true,
    }),
  ),

  on(
    UserActions.checkCustomerExistsFailure,
    UserActions.checkCustomerExistsSuccess,
    UserActions.sendPassToCustomerSuccess,
    UserActions.validateCustomerFailure,
    UserActions.validateCustomerSuccess,
    UserActions.registerFailure,
    UserActions.registerSuccess,
    UserActions.getApplePassFailure,
    UserActions.getApplePassSuccess,
    UserActions.getGooglePassFailure,
    UserActions.getCustomerDataFailure,
    UserActions.getCustomerRewardsFailure,
    UserActions.sendValidationEmailSuccess,
    UserActions.sendValidationEmailFailure,
    UserActions.updateCustomerPreferencesFailure,
    UserActions.updateCustomerProfileFailure,
    UserActions.getGooglePassSuccess,
    UserActions.openShopLinkSuccess,
    UserActions.getCustomerTransactionsFailure,
    UserActions.openShopLinkFailure,
    (state): UserState => ({
      ...state,
      loading: false,
    }),
  ),

  on(
    UserActions.claimCustomerRewardFailure,
    (state, { error }): UserState => ({
      ...state,
      error: getError(error),
      errorParams: getErrorParams(error),
      loading: false,
    }),
  ),

  on(
    UserActions.sendPassToCustomerFailure,
    (state, { error }): UserState => ({
      ...state,
      error: getError(error),
      errorParams: getErrorParams(error),
      loading: false,
    }),
  ),

  on(
    UserActions.setError,
    (state, { error }): UserState => ({
      ...state,
      error: getError(error),
      errorParams: getErrorParams(error),
    }),
  ),

  on(
    UserActions.getCustomerDataSuccess,
    (state, { customerData }): UserState => ({
      ...state,
      loading: false,
      customerData,
    }),
  ),

  on(UserActions.getCustomerRewardsSuccess, (state, { rewards }): UserState => {
    const entities: Rewards = {};
    rewards.forEach(reward => (entities[reward.id] = reward));

    return {
      ...state,
      loading: false,
      rewards: entities,
    };
  }),

  on(UserActions.updateCustomerPreferencesSuccess, (state, { customer }) => ({
    ...state,
    customerData: {
      ...state.customerData,
      customer,
    },
    loading: false,
  })),

  on(
    UserActions.claimCustomerRewardSuccess,
    (state, { claimedReward }): UserState => ({
      ...state,
      loading: false,
      claimedRewards: {
        ...state.claimedRewards,
        [claimedReward?.externalRewardId]: claimedReward,
      },
    }),
  ),

  on(
    UserActions.emptyClaimedRewards,
    (state): UserState => ({
      ...state,
      claimedRewards: {},
    }),
  ),

  on(
    UserActions.setReferral,
    (state, { referral }): UserState => ({
      ...state,
      referral,
    }),
  ),

  on(UserActions.setUTMValues, (state, { utm }) => ({ ...state, utm })),

  on(UserActions.getCustomerTransactionsSuccess, (state, { transactions }) => ({ ...state, transactions, loading: false })),

  on(UserActions.getCustomerCustomerCodes, (state): UserState => ({ ...state, customerCodesLoading: true })),

  on(UserActions.getCustomerCustomerCodesSuccess, (state, { customerCodes }): UserState => ({ ...state, customerCodes, customerCodesLoading: false })),

  on(UserActions.getCustomerCustomerCodesFailure, (state): UserState => ({ ...state, customerCodesLoading: false })),

  on(UserActions.getProfileForm, (state): UserState => ({
    ...state,
    profileForm: { ...state.profileForm, loading: true, error: null },
  })),

  on(UserActions.getProfileFormSuccess, (state, { profileForm }): UserState => ({
    ...state,
    profileForm: { ...state.profileForm, data: profileForm, loading: false },
  })),

  on(UserActions.getProfileFormFailure, (state, { error }): UserState => ({
    ...state,
    profileForm: { ...state.profileForm, loading: false, error: getError(error) },
  })),

  on(UserActions.updateProfileForm, (state, { updateProfileFormDto }): UserState => ({
    ...state,
    profileForm: {
      ...state.profileForm,
      savingSectionId: updateProfileFormDto.sections[0]?.sectionId ?? null,
      errors: [],
      error: null,
    },
  })),

  on(UserActions.updateProfileFormSuccess, (state, { profileForm }): UserState => ({
    ...state,
    profileForm: { ...state.profileForm, data: profileForm, savingSectionId: null, outdated: false },
  })),

  on(UserActions.updateProfileFormFailure, (state, { error }): UserState => {
    const outdated = isProfileFormOutdated(error);
    const errors = outdated ? [] : getProfileFormErrors(error);

    return {
      ...state,
      profileForm: {
        ...state.profileForm,
        savingSectionId: null,
        outdated,
        errors,
        error: outdated || errors.length > 0 ? null : getError(error),
      },
    };
  }),

  on(UserActions.resetProfileFormOutdated, (state): UserState => ({
    ...state,
    profileForm: { ...state.profileForm, outdated: false },
  })),

  on(AppActions.executeCustomEarningFlowSuccess, (state, { engagementFlowName, response }): UserState => {
    if (!state.customerData) return state;
    const existingRun = state.customerData.engagementFlows?.[engagementFlowName];
    return {
      ...state,
      customerData: {
        ...state.customerData,
        customer: {
          ...state.customerData.customer,
          loyaltyData: {
            ...state.customerData.customer.loyaltyData,
            balance: (state.customerData.customer.loyaltyData?.balance ?? 0) + response.pointsAwarded,
          },
        },
        engagementFlows: {
          ...state.customerData.engagementFlows,
          [engagementFlowName]: {
            properties: existingRun?.properties ?? {},
            count: (existingRun?.count ?? 0) + 1,
          },
        },
      },
    };
  }),
);
